import { Button } from '@/components/ui/button';
import { Helmet } from 'react-helmet-async';
import { CanonicalTag } from '@/components/CanonicalTag';
import { StructuredData } from '@/components/StructuredData';
import { useNavigate } from 'react-router-dom';
import { trackEvent } from '@/components/GoogleAnalytics';
import finishLineImage from '@/assets/triathlon-finish-line.jpg';

const Start = () => {
  const navigate = useNavigate();

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: 'Sink Bike Run - Custom Triathlon Checklist',
    description: 'A simple, customizable checklist built by a triathlete — so you can stay organized, stress less, and focus on your race.',
    applicationCategory: 'LifestyleApplication',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD'
    }
  };

  const handleStartChecklist = () => {
    trackEvent('cta_create_checklist_clicked', { source: 'start_page_hero' });
    navigate('/#create-checklist');
  };

  return (
    <>
      <Helmet>
        <title>Create Your Triathlon Checklist | Get Started Free</title>
        <meta 
          name="description" 
          content="A simple, customizable checklist built by a triathlete — so you can stay organized, stress less, and focus on your race." 
        />
        <meta property="og:title" content="Create Your Triathlon Checklist | Get Started Free" />
        <meta 
          property="og:description" 
          content="A simple, customizable checklist built by a triathlete — so you can stay organized, stress less, and focus on your race." 
        />
        <meta property="og:type" content="website" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Create Your Triathlon Checklist | Get Started Free" />
        <meta name="twitter:description" content="A simple, customizable checklist built by a triathlete — so you can stay organized, stress less, and focus on your race." />
        <meta name="twitter:image" content="https://sinkbikerun.com/og-image.jpg" />
        
        {/* Preload hero image */}
        <link rel="preload" as="image" href={finishLineImage} fetchPriority="high" />
      </Helmet>
      <CanonicalTag url="/start" />
      <StructuredData data={structuredData} />

      <div className="min-h-screen bg-background">
        <main>
          {/* Hero Section */}
          <section className="relative min-h-[50vh] sm:min-h-[60vh] flex items-center justify-center overflow-hidden">
          <img 
            src={finishLineImage}
            alt="Triathlon athletes preparing for race"
            className="absolute inset-0 w-full h-full"
            data-hero-image="true"
            style={{ objectFit: 'cover' }}
            loading="eager"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-foreground/40" />
          
          <div className="relative z-10 max-w-4xl mx-auto px-6 py-8 sm:py-0 text-center">
            <h1 className="text-3xl sm:text-4xl md:text-6xl font-display font-semibold text-white mb-4 sm:mb-6 tracking-tight">
              Train Hard. Pack Smart. Race Confident.
            </h1>
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl font-body text-white/90 mb-8 sm:mb-10 md:mb-12 max-w-2xl mx-auto leading-relaxed">
              Build a custom triathlon checklist in minutes with event-specific templates — make sure nothing is forgotten so you can perform at your best.
            </p>
            <Button 
              onClick={handleStartChecklist}
              size="lg"
              className="w-full md:w-auto bg-primary hover:bg-primary-dark text-primary-foreground h-11 sm:h-12 md:h-14 px-5 sm:px-6 md:px-8 text-base md:text-lg rounded-sm transition-all duration-200 shadow-modern hover:shadow-hero"
            >
              Create Your Checklist
            </Button>
          </div>
        </section>

        {/* Intro Section */}
        <section className="py-6 sm:py-8 md:py-10 px-6 bg-background">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-base sm:text-lg font-body text-foreground/80 leading-[1.7]">
              Even one forgotten item can compromise your race. Sink Bike Run tracks everything you need — gear, nutrition, and other essentials — so you arrive ready and focused.
            </p>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-8 md:py-10 px-6 bg-secondary">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8 md:gap-12">
              <div className="text-center">
                <h2 className="text-lg sm:text-xl font-display font-semibold text-foreground mb-4">
                  Stay organized for any race.
                </h2>
                <p className="text-base font-body text-foreground/70 leading-[1.65]">
                  Sprint, Olympic, Half, or Full — use event templates or build your own checklist that adapts to your distance.
                </p>
              </div>

              <div className="text-center">
                <h2 className="text-lg sm:text-xl font-display font-semibold text-foreground mb-4">
                  Make it yours.
                </h2>
                <p className="text-base font-body text-foreground/70 leading-[1.65]">
                  Customize gear and nutrition to fit your goals.
                </p>
              </div>

              <div className="text-center">
                <h2 className="text-lg sm:text-xl font-display font-semibold text-foreground mb-4">
                  Save time, perfect your process.
                </h2>
                <p className="text-base font-body text-foreground/70 leading-[1.65]">
                  Reuse your checklist and refine it every time you race.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Section */}
        <section className="py-8 md:py-10 px-6 bg-background">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-bold text-foreground mb-8">
              Built by an athlete, for athletes.
            </h2>
            <p className="text-lg font-body text-foreground/80 leading-[1.7] mb-6">
              Created by a triathlete who knows that being fully prepared is the only way to race your best.
            </p>
            <p className="text-lg font-body text-foreground/80 leading-[1.7]">
              This tool is free, simple, and built with one goal — help you show up to the starting line prepared and confident.
            </p>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-8 md:py-10 px-6 bg-secondary">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-bold text-foreground mb-8">
              Forget nothing. Race your best.
            </h2>
            <Button 
              onClick={handleStartChecklist}
              size="lg"
              className="w-full md:w-auto bg-primary hover:bg-primary-dark text-primary-foreground h-12 md:h-14 px-6 md:px-8 text-base md:text-lg rounded-sm transition-all duration-200 shadow-modern hover:shadow-hero"
            >
              Create Your Checklist
            </Button>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 px-6 bg-background border-t border-border">
          <div className="max-w-6xl mx-auto text-center">
            <p className="text-sm text-foreground/60 font-light">
            © 2018-2025 Sink Bike Run
          </p>
        </div>
      </footer>
        </main>
      </div>
    </>
  );
};

export default Start;
